/**
 * AssignPlanDialog — assigns an existing global drill plan to the currently selected client.
 *
 * @see docs/features/260331-automated-driller-mvp/FEATURE_SPEC.md G2, A1
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, UserCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useClient } from '@/context/client-context';
import { createDrillPlanAssignment } from './services/drill-plans-api';
import { useToast } from '@/hooks/use-toast';
import logger from '@/lib/logging';
import type { DrillPlan } from './types/drill-plan-types';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';

interface AssignPlanDialogProps {
  plan: DrillPlan | null;
  isOpen: boolean;
  onClose: () => void;
  onAssigned?: () => void;
}

const AssignPlanDialog: React.FC<AssignPlanDialogProps> = ({
  plan,
  isOpen,
  onClose,
  onAssigned,
}) => {
  const { user } = useAuth();
  const { client } = useClient();
  const { toast } = useToast();
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAssign = async () => {
    if (!plan || !client || !user?.uid) return;
    setIsSaving(true);

    try {
      await createDrillPlanAssignment(client.id, {
        drillPlanDefinitionId: plan.id,
        clientId: client.id,
        assigningPractitionerUid: user.uid,
        drillPlanName: plan.name,
        notes: notes.trim() || undefined,
      });

      logger.info('drill-plan-assignment:create', {
        planId: plan.id,
        clientId: client.id,
        assignedByUid: user.uid,
      });

      toast({
        title: 'Plan assigned!',
        description: `"${plan.name}" has been assigned to ${clientDisplayLabel(client)}.`,
      });

      setNotes('');
      onAssigned?.();
      onClose();
    } catch (err) {
      logger.error('drill-plan-assignment:create:error', err);
      toast({
        title: 'Assignment failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Drill Plan</DialogTitle>
          <DialogDescription>
            Assign <strong>{plan?.name}</strong> to{' '}
            <strong>{client ? clientDisplayLabel(client) : 'selected client'}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="assign-notes" className="text-sm">
              Notes (optional)
            </Label>
            <Textarea
              id="assign-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add practitioner notes for this assignment…"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={isSaving || !client} className="gap-2">
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <UserCheck className="h-4 w-4" />
              )}
              {isSaving ? 'Assigning…' : 'Assign to Client'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignPlanDialog;
